import React, { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, RefreshCw, AlertTriangle, TrendingUp, Ship, PackageCheck } from 'lucide-react';

/**
 * FirstSaleSavings — CHA-365. Imports → First Sale → Savings: duty+tariff savings from First Sale
 * over FS/FS3 CPLAN lines (operator model 2026-07-21):
 *  - ISSUED / SHIPPED (open): PROJECTED — FOB × assumed 15% first-sale discount × (duty% + tariff%);
 *  - RECEIVED: ACTUAL — (system FOB − customs declared FOB) × (duty% + tariff%).
 * Tiles per stage, actuals by received month, and a by-vendor breakdown (beneficiary → program
 * vendor via the First Sale vendor lookup).
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);
const pct = (num, den) => (Number.isFinite(Number(num)) && Number(den) > 0 ? `${(100 * (Number(num) / Number(den))).toFixed(1)}%` : '—');

/** Render the First Sale savings dashboard and call the secured tenant-aware backend. */
export default function FirstSaleSavings({ userProfile } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getFirstSaleSavings', { timeout: 150000 });
      const tenantPayload = isPlatformRole ? { tenantId } : {};
      const res = await fn(tenantPayload);
      setData(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isPlatformRole, tenantId]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600 flex-1">
          Duty + tariff saved by First Sale on FS/FS3 CPLAN orders — open orders plus receipts from 7/1/26
          (the SP27 program start). Open orders (Issued / Shipped) are
          <span className="font-medium"> projected</span> at an assumed {data?.assumedDiscountPct ?? 15}% first-sale
          discount on FOB; <span className="font-medium">received</span> uses the actual customs declared value
          (fs_cost only when fs_flag is Y and fs_cost is positive, otherwise FOBHA) — saving = discount × duty%
          plus discount × tariff%.
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1.5 rounded shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && !data && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Computing savings…</div>}

      {data && (
        <>
          {/* Stage tiles: how we start (issued) → in transit → what actually landed */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Projected — Issued', icon: TrendingUp, val: t.projectedIssued, sub: `on ${money(t.fobIssued)} open FOB · assumed ${data.assumedDiscountPct}%`, cls: 'text-blue-700' },
              { label: 'Projected — Shipped', icon: Ship, val: t.projectedShipped, sub: `on ${money(t.fobShipped)} FOB on the water · assumed ${data.assumedDiscountPct}%`, cls: 'text-cyan-700' },
              { label: 'Actual — Received', icon: PackageCheck, val: t.actualReceived, sub: `${money(t.fobReceived)} system FOB → ${money(t.customsReceived)} customs FOB · saved ${pct(t.actualDiscountFob, t.fobMeasured ?? t.fobReceived)}`, cls: 'text-green-700' },
            ].map(({ label, icon: Icon, val, sub, cls }) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Icon className="h-4 w-4" />{label}</div>
                <div className={`text-2xl font-semibold tabular-nums mt-1 ${cls}`}>{money(val)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {(data.quality?.receivedMissingCustomsValue > 0 || data.quality?.zeroRateRows > 0) && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Data gaps counted as $0 savings: {data.quality.receivedMissingCustomsValue} received line(s) without a usable customs value · {data.quality.zeroRateRows} bucket(s) with 0% duty+tariff.
            </div>
          )}

          {/* Actual savings by received month — the program's trajectory */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="px-3 py-2 border-b border-slate-200 text-sm font-semibold">Actual savings by received month</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-slate-500 text-xs bg-slate-50">
                  <tr>{['Month', 'System FOB', 'Customs FOB', 'Saved %', 'Duty saved', 'Tariff saved', 'Total saved'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.byMonth || []).map((m) => (
                    <tr key={m.month} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-mono text-xs">{m.month}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(m.fob)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(m.customs)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{pct(m.discount, m.fobMeasured ?? m.fob)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-green-700">{money(m.dutySaving)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-green-700">{money(m.tariffSaving)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium text-green-700">{money(m.saving)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By vendor — projected (open) vs actual (received) */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="px-3 py-2 border-b border-slate-200 text-sm font-semibold">By vendor</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-slate-500 text-xs bg-slate-50">
                  <tr>{['Vendor / BC code', 'Open FOB', 'Projected saving', 'System FOB', 'Customs FOB', 'Saved %', 'Duty saved', 'Tariff saved', 'Actual saving'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.byVendor || []).map((v) => (
                    <tr key={v.vendor} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-medium">
                        {v.inProgram === false ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono text-xs">{v.vendor}</span>
                            <span title="An LC beneficiary code not claimed by any First Sale program vendor — add it to a vendor in Admin → Lookup Manager to show the vendor name here" className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-800 cursor-help">not in program</span>
                          </span>
                        ) : v.approved === false ? (
                          <span className="inline-flex items-center gap-1.5">
                            {v.vendor}
                            <span title="In the First Sale program but NOT approved yet — FS/FS3 usage under this vendor flags in Production → Verification" className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 cursor-help">not approved</span>
                          </span>
                        ) : v.vendor}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(v.fobOpen)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-blue-700">{money(v.projected)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(v.fobReceived)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(v.customsReceived)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{pct(v.actualDiscountFob, v.fobMeasured ?? v.fobReceived)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-green-700">{money(v.actualDutySaving)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-green-700">{money(v.actualTariffSaving)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium text-green-700">{money(v.actual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
